import Component from "../../Component";
import { Room, Seat } from "../types";
import BaseApi from "./BaseApi";
import { RoomUtils } from "../utils/RoomUtils";

/**
 * Room API - Handles all room-related operations
 * Extends BaseApi for consistent error handling and authentication
 * Follows Single Responsibility Principle
 */
export default class RoomApi extends BaseApi {
    constructor(component: Component) {
        super(component);
    }

    public async fetchById(seatId: string): Promise<Room> {
        const endpoint = `/rooms/${seatId}`;

        return this.fetchWithAuth<Room>(endpoint);
    }
    public async fetchAll(): Promise<Room[]> {
        const endpoint = "/rooms";

        return this.fetchWithAuth<Room[]>(endpoint);
    }

    /**
     * Fetches seats by room ID
     * @param roomId Room ID to get seats for
     * @returns Promise<Seat[]> Array of seats in the room
     */
    public async fetchSeatsByRoom(roomId: string): Promise<Seat[]> {
        try {
            return await this.fetchWithAuth<Seat[]>(`/rooms/${roomId}/seats`);
        } catch (error) {
            console.error("Error fetching seats by room:", error);
            return [];
        }
    }

    /**
     * Fetches rooms by floor plan ID
     * @param floorId Floor plan ID to filter rooms by
     * @returns Promise<any[]> Array of rooms for the specified floor
     */
    public async fetchRoomsByFloor(floorId: string): Promise<Room[]> {
        try {
            // Get all rooms and filter by floor plan ID
            const allRooms = await this.fetchWithAuth<Room[]>("/rooms");

            // Filter rooms by floor plan ID
            const filteredRooms = allRooms.filter(
                (room: Room) => room.floorPlan && room.floorPlan.id === floorId
            );

            return filteredRooms;
        } catch (error) {
            console.error("Error fetching rooms by floor:", error);
            return [];
        }
    }

    /**
     * Fetches conference rooms based on room types
     * Conference rooms are identified by having a room type that indicates they are meeting/conference rooms
     * Excludes flex/training rooms that have individual desks
     * @returns Promise<Room[]> Array of conference rooms
     */
    public async fetchConferenceRooms(): Promise<Room[]> {
        try {
            const allRooms = await this.fetchWithAuth<Room[]>("/rooms");

            // Use centralized conference room detection logic
            const conferenceRooms = RoomUtils.filterConferenceRooms(allRooms);

            console.log(
                "Conference rooms found:",
                conferenceRooms.map((r) => ({
                    id: r.id,
                    name: r.name,
                    description: r.description,
                    roomTypes: r.roomTypes?.map((rt) => rt.name)
                }))
            );

            return conferenceRooms;
        } catch (error) {
            console.error("Error fetching conference rooms:", error);
            return [];
        }
    }

    /**
     * Checks if a room is a conference room based on its room types
     * Excludes flex/training rooms that have individual desks
     * @param roomId Room ID to check
     * @returns Promise<boolean> True if the room is a conference room
     */
    public async isConferenceRoom(roomId: string): Promise<boolean> {
        try {
            const room = await this.fetchById(roomId);

            // Use centralized conference room detection logic
            return RoomUtils.isConferenceRoom(room);
        } catch (error) {
            console.error("Error checking if room is conference room:", error);
            return false;
        }
    }
}
